import { useState, useEffect } from 'react';
import { Activity, User, Calendar, Filter, RefreshCw } from 'lucide-react';
import activitiesService, { UserActivity, ActivityStats } from '../services/activitiesService';

export const ActivitiesPage = () => {
   const [activities, setActivities] = useState<UserActivity[]>([]);
   const [stats, setStats] = useState<ActivityStats | null>(null);
   const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, hasMore: false });
   const [loading, setLoading] = useState(true);
   const [statsLoading, setStatsLoading] = useState(true);
   const [filters, setFilters] = useState({
      page: 1,
      limit: 10,
      actionType: '',
      category: '',
      startDate: '',
      endDate: '',
   });

   // Fetch activities
   const fetchActivities = async () => {
      try {
         setLoading(true);
         const response = await activitiesService.getActivities(filters);
         if (response.success) {
            setActivities(response.data.activities);
            setPagination(response.data.pagination);
         }
      } catch (error) {
         console.error('Failed to fetch activities:', error);
      } finally {
         setLoading(false);
      }
   };

   // Fetch statistics
   const fetchStats = async () => {
      try {
         setStatsLoading(true);
         const response = await activitiesService.getActivityStats();
         if (response.success) {
            setStats(response.data);
         }
      } catch (error) {
         console.error('Failed to fetch activity stats:', error);
      } finally {
         setStatsLoading(false);
      }
   };

   useEffect(() => {
      fetchActivities();
      fetchStats();
   }, []);

   useEffect(() => {
      fetchActivities();
   }, [filters]);

   const handleFilterChange = (key: string, value: string) => {
      setFilters((prev) => ({
         ...prev,
         [key]: value,
         page: 1, // Reset to first page when filtering
      }));
   };

   // Mapping for readable action type labels
   const getActionTypeLabel = (actionType: string) => {
      const labels = {
         page_view: 'Page View',
         view_content: 'View Content',
         click: 'Click',
         scroll: 'Scroll',
         search: 'Search',
         filter: 'Filter',
         sort: 'Sort',
         share_content: 'Share Content',
         like_content: 'Like Content',
         comment: 'Comment',
         download: 'Download',
         create_trip: 'Create Trip',
         edit_trip: 'Edit Trip',
         delete_trip: 'Delete Trip',
         join_trip: 'Join Trip',
         leave_trip: 'Leave Trip',
         view_trip: 'View Trip',
         create_event: 'Create Event',
         edit_event: 'Edit Event',
         delete_event: 'Delete Event',
         join_event: 'Join Event',
         leave_event: 'Leave Event',
         view_event: 'View Event',
         upload_photo: 'Upload Photo',
         delete_photo: 'Delete Photo',
         view_photo: 'View Photo',
         transform_photo: 'Transform Photo',
         signup: 'Sign Up',
         login: 'Login',
         logout: 'Logout',
         profile_update: 'Profile Update',
         purchase: 'Purchase',
         subscription: 'Subscription',
         payment_method_add: 'Add Payment Method',
         send_invitation: 'Send Invitation',
         accept_invitation: 'Accept Invitation',
         connect_user: 'Connect User',
         error: 'Error',
         api_error: 'API Error',
         custom: 'Custom',
      };
      return (
         labels[actionType] ||
         actionType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      );
   };

   // Mapping for readable category labels
   const getCategoryLabel = (category: string) => {
      const labels = {
         navigation: 'Navigation',
         user_interaction: 'User Interaction',
         content: 'Content',
         trip: 'Trip',
         event: 'Event',
         photo: 'Photo',
         user: 'User',
         payment: 'Payment',
         social: 'Social',
         error: 'Error',
         custom: 'Custom',
      };
      return (
         labels[category] || category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      );
   };

   // Mapping for readable page labels
   const getPageLabel = (page: string, pagePath?: string) => {
      const labels = {
         authentication: 'Authentication Page',
         login: 'Login Page',
         signup: 'Sign Up Page',
         dashboard: 'Dashboard',
         profile: 'Profile Page',
         photo_upload: 'Photo Upload Page',
         trip_create: 'Create Trip Page',
         trip_edit: 'Edit Trip Page',
         event_create: 'Create Event Page',
         event_edit: 'Edit Event Page',
         settings: 'Settings Page',
         payment: 'Payment Page',
         subscription: 'Subscription Page',
         invitations: 'Invitations Page',
         navigation: 'Navigation',
         unknown: 'Unknown Page',
      };

      // Try to get a readable label from the page or pagePath
      const pageToCheck = page || pagePath || 'unknown';

      // Check direct mapping first
      if (labels[pageToCheck]) {
         return labels[pageToCheck];
      }

      // Try to extract from pagePath (e.g., "/dashboard" -> "Dashboard")
      if (pagePath && pagePath.startsWith('/')) {
         const pathSegment = pagePath.substring(1).split('/')[0];
         if (labels[pathSegment]) {
            return labels[pathSegment];
         }
         // Convert path to readable format
         return pathSegment.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) + ' Page';
      }

      // Fallback: convert underscore to space and title case
      return pageToCheck.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
   };

   const getActionTypeColor = (actionType: string) => {
      const colors = {
         page_view: 'bg-blue-100 text-blue-800',
         view_content: 'bg-indigo-100 text-indigo-800',
         click: 'bg-green-100 text-green-800',
         scroll: 'bg-gray-100 text-gray-800',
         search: 'bg-yellow-100 text-yellow-800',
         filter: 'bg-orange-100 text-orange-800',
         sort: 'bg-orange-100 text-orange-800',
         share_content: 'bg-purple-100 text-purple-800',
         like_content: 'bg-pink-100 text-pink-800',
         comment: 'bg-blue-100 text-blue-800',
         download: 'bg-green-100 text-green-800',
         create_trip: 'bg-emerald-100 text-emerald-800',
         edit_trip: 'bg-yellow-100 text-yellow-800',
         delete_trip: 'bg-red-100 text-red-800',
         join_trip: 'bg-green-100 text-green-800',
         leave_trip: 'bg-orange-100 text-orange-800',
         view_trip: 'bg-blue-100 text-blue-800',
         create_event: 'bg-emerald-100 text-emerald-800',
         edit_event: 'bg-yellow-100 text-yellow-800',
         delete_event: 'bg-red-100 text-red-800',
         join_event: 'bg-green-100 text-green-800',
         leave_event: 'bg-orange-100 text-orange-800',
         view_event: 'bg-blue-100 text-blue-800',
         upload_photo: 'bg-indigo-100 text-indigo-800',
         delete_photo: 'bg-red-100 text-red-800',
         view_photo: 'bg-blue-100 text-blue-800',
         transform_photo: 'bg-purple-100 text-purple-800',
         signup: 'bg-green-100 text-green-800',
         login: 'bg-emerald-100 text-emerald-800',
         logout: 'bg-red-100 text-red-800',
         profile_update: 'bg-yellow-100 text-yellow-800',
         purchase: 'bg-green-100 text-green-800',
         subscription: 'bg-blue-100 text-blue-800',
         payment_method_add: 'bg-indigo-100 text-indigo-800',
         send_invitation: 'bg-purple-100 text-purple-800',
         accept_invitation: 'bg-green-100 text-green-800',
         connect_user: 'bg-blue-100 text-blue-800',
         error: 'bg-red-100 text-red-800',
         api_error: 'bg-red-100 text-red-800',
         custom: 'bg-gray-100 text-gray-800',
         default: 'bg-gray-100 text-gray-800',
      };
      return colors[actionType] || colors.default;
   };

   const formatTimestamp = (timestamp: string) => {
      const date = new Date(timestamp);
      return {
         date: date.toLocaleDateString(),
         time: date.toLocaleTimeString(),
         relative: getRelativeTime(date),
      };
   };

   const getRelativeTime = (date: Date) => {
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
   };

  return (
     <div className='space-y-4'>
        {/* Header */}
        <div>
           <h1 className='text-xl font-bold text-gray-900'>User Activities</h1>
           <p className='mt-0.5 text-xs text-gray-500'>
              Monitor user activities and track platform usage across all users.
           </p>
        </div>

        {/* Statistics Cards */}
        {!statsLoading && stats && (
           <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
              <div className='bg-white overflow-hidden shadow rounded-lg'>
                 <div className='p-3'>
                    <div className='flex items-center'>
                       <div className='flex-shrink-0'>
                          <Activity className='h-5 w-5 text-blue-600' />
                       </div>
                       <div className='ml-3 w-0 flex-1'>
                          <dl>
                             <dt className='text-xs font-medium text-gray-500 truncate'>
                                Total Activities
                             </dt>
                             <dd className='text-base font-medium text-gray-900'>
                                {stats.totalActivities.toLocaleString()}
                             </dd>
                          </dl>
                       </div>
                    </div>
                 </div>
              </div>

              <div className='bg-white overflow-hidden shadow rounded-lg'>
                 <div className='p-3'>
                    <div className='flex items-center'>
                       <div className='flex-shrink-0'>
                          <User className='h-5 w-5 text-green-600' />
                       </div>
                       <div className='ml-3 w-0 flex-1'>
                          <dl>
                             <dt className='text-xs font-medium text-gray-500 truncate'>
                                Total Users
                             </dt>
                             <dd className='text-base font-medium text-gray-900'>
                                {stats.uniqueUsers.toLocaleString()}
                             </dd>
                          </dl>
                       </div>
                    </div>
                 </div>
              </div>

              <div className='bg-white overflow-hidden shadow rounded-lg'>
                 <div className='p-3'>
                    <div className='flex items-center'>
                       <div className='flex-shrink-0'>
                          <Calendar className='h-5 w-5 text-purple-600' />
                       </div>
                       <div className='ml-3 w-0 flex-1'>
                          <dl>
                             <dt className='text-xs font-medium text-gray-500 truncate'>
                                Action Types
                             </dt>
                             <dd className='text-base font-medium text-gray-900'>
                                {Object.keys(stats.actionTypes).length}
                             </dd>
                          </dl>
                       </div>
                    </div>
                 </div>
              </div>

              <div className='bg-white overflow-hidden shadow rounded-lg'>
                 <div className='p-3'>
                    <div className='flex items-center'>
                       <div className='flex-shrink-0'>
                          <RefreshCw className='h-5 w-5 text-orange-600' />
                       </div>
                       <div className='ml-3 w-0 flex-1'>
                          <dl>
                             <dt className='text-xs font-medium text-gray-500 truncate'>
                                Pages Visited
                             </dt>
                             <dd className='text-base font-medium text-gray-900'>
                                {Object.keys(stats.pages).length}
                             </dd>
                          </dl>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        )}

        {/* Filters */}
        <div className='bg-white shadow rounded-lg'>
           <div className='px-3 py-3 sm:p-4'>
              <h3 className='text-base leading-6 font-medium text-gray-900 mb-3'>
                 <Filter className='inline-block w-4 h-4 mr-2' />
                 Filters
              </h3>
              <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                 <div>
                    <label className='block text-xs font-medium text-gray-700 mb-1'>
                       Action Type
                    </label>
                    <select
                       value={filters.actionType}
                       onChange={(e) => handleFilterChange('actionType', e.target.value)}
                       className='block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5'
                    >
                       <option value=''>All Actions</option>
                       <option value='page_view'>Page View</option>
                       <option value='view_content'>View Content</option>
                       <option value='click'>Click</option>
                       <option value='login'>Login</option>
                       <option value='logout'>Logout</option>
                       <option value='signup'>Sign Up</option>
                       <option value='create_trip'>Create Trip</option>
                       <option value='upload_photo'>Upload Photo</option>
                       <option value='error'>Error</option>
                    </select>
                 </div>

                 <div>
                    <label className='block text-xs font-medium text-gray-700 mb-1'>Category</label>
                    <select
                       value={filters.category}
                       onChange={(e) => handleFilterChange('category', e.target.value)}
                       className='block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5'
                    >
                       <option value=''>All Categories</option>
                       <option value='navigation'>Navigation</option>
                       <option value='user_interaction'>User Interaction</option>
                       <option value='authentication'>Authentication</option>
                       <option value='content'>Content</option>
                       <option value='trip'>Trip</option>
                       <option value='photo'>Photo</option>
                       <option value='error'>Error</option>
                    </select>
                 </div>

                 <div>
                    <label className='block text-xs font-medium text-gray-700 mb-1'>
                       Start Date
                    </label>
                    <input
                       type='date'
                       value={filters.startDate}
                       onChange={(e) => handleFilterChange('startDate', e.target.value)}
                       className='block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5'
                    />
                 </div>

                 <div>
                    <label className='block text-xs font-medium text-gray-700 mb-1'>End Date</label>
                    <input
                       type='date'
                       value={filters.endDate}
                       onChange={(e) => handleFilterChange('endDate', e.target.value)}
                       className='block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-xs py-1.5'
                    />
                 </div>
              </div>

              <div className='mt-3 flex justify-between items-center'>
                 <button
                    onClick={() =>
                       setFilters({
                          page: 1,
                          limit: 10,
                          actionType: '',
                          category: '',
                          startDate: '',
                          endDate: '',
                       })
                    }
                    className='text-xs text-gray-500 hover:text-gray-700'
                 >
                    Clear Filters
                 </button>
                 <button
                    onClick={() => {
                       fetchActivities();
                       fetchStats();
                    }}
                    className='inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                 >
                    <RefreshCw className='w-3 h-3 mr-1' />
                    Refresh
                 </button>
              </div>
           </div>
        </div>

        {/* Activities Table */}
        <div className='bg-white shadow rounded-lg'>
           <div className='px-3 py-3 sm:p-4'>
              <h3 className='text-base leading-6 font-medium text-gray-900 mb-3'>
                 Recent Activities ({activities.length} results)
              </h3>

              {loading ? (
                 <div className='text-center py-4'>
                    <RefreshCw className='animate-spin h-6 w-6 text-gray-400 mx-auto' />
                    <p className='mt-1 text-xs text-gray-500'>Loading activities...</p>
                 </div>
              ) : activities.length === 0 ? (
                 <div className='text-center py-4'>
                    <Activity className='h-8 w-8 text-gray-400 mx-auto' />
                    <p className='mt-1 text-xs text-gray-500'>
                       No activities found matching your filters.
                    </p>
                 </div>
              ) : (
                 <div className='overflow-hidden'>
                    <table className='min-w-full divide-y divide-gray-200'>
                       <thead className='bg-gray-50'>
                          <tr>
                             <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                User
                             </th>
                             <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Action & Category
                             </th>
                             <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Page/Source
                             </th>
                             <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Details
                             </th>
                             <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Timestamp
                             </th>
                             <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                                Device
                             </th>
                          </tr>
                       </thead>
                       <tbody className='bg-white divide-y divide-gray-200'>
                          {activities.map((activity) => {
                             const timeInfo = formatTimestamp(activity.timestamp);
                             return (
                                <tr key={activity.id} className='hover:bg-gray-50'>
                                   <td className='px-3 py-2 whitespace-nowrap'>
                                      <div className='flex items-center'>
                                         {activity.user.photoURL && (
                                            <img
                                               className='h-6 w-6 rounded-full mr-2'
                                               src={activity.user.photoURL}
                                               alt={`${activity.user.firstName} ${activity.user.lastName}`}
                                               onError={(e) => {
                                                  e.currentTarget.style.display = 'none';
                                               }}
                                            />
                                         )}
                                         <div>
                                            <div className='text-xs font-medium text-gray-900'>
                                               {activity.user.firstName} {activity.user.lastName}
                                            </div>
                                            <div className='text-xs text-gray-500'>
                                               {activity.user.email}
                                            </div>
                                            <div className='text-xs text-gray-400'>
                                               {activity.userId.substring(0, 8)}...
                                            </div>
                                         </div>
                                      </div>
                                   </td>
                                   <td className='px-3 py-2 whitespace-nowrap'>
                                      <span
                                         className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${getActionTypeColor(
                                            activity.actionType
                                         )}`}
                                      >
                                         {getActionTypeLabel(activity.actionType)}
                                      </span>
                                      {activity.category && (
                                         <div className='text-xs text-gray-500 mt-0.5'>
                                            {getCategoryLabel(activity.category)}
                                         </div>
                                      )}
                                   </td>
                                   <td className='px-6 py-4 whitespace-nowrap'>
                                      <div className='text-sm text-gray-900'>
                                         {getPageLabel(activity.page, activity.metadata?.pagePath)}
                                      </div>
                                      <div className='text-xs text-gray-500'>
                                         Source:{' '}
                                         {activity.source
                                            .replace(/_/g, ' ')
                                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                                      </div>
                                      {activity.metadata?.pageTitle && (
                                         <div className='text-xs text-gray-400'>
                                            {activity.metadata.pageTitle}
                                         </div>
                                      )}
                                   </td>
                                   <td className='px-3 py-2 whitespace-nowrap'>
                                      {activity.description && (
                                         <div className='text-xs text-gray-900 mb-0.5'>
                                            {activity.description.substring(0, 40)}
                                            {activity.description.length > 40 ? '...' : ''}
                                         </div>
                                      )}
                                      {activity.environment && (
                                         <div className='text-xs text-gray-500'>
                                            {activity.environment}
                                         </div>
                                      )}
                                      {activity.metadata?.authMethod && (
                                         <div className='text-xs text-blue-600'>
                                            {activity.metadata.authMethod}
                                         </div>
                                      )}
                                      {activity.metadata?.errorCode && (
                                         <div className='text-xs text-red-600'>
                                            {activity.metadata.errorCode}
                                         </div>
                                      )}
                                      {activity.location && (
                                         <div className='text-xs text-green-600'>
                                            📍 {activity.location.city}
                                         </div>
                                      )}
                                   </td>
                                   <td className='px-3 py-2 whitespace-nowrap'>
                                      <div className='text-xs text-gray-900'>
                                         {timeInfo.relative}
                                      </div>
                                      <div className='text-xs text-gray-500'>{timeInfo.date}</div>
                                   </td>
                                   <td className='px-3 py-2 whitespace-nowrap'>
                                      <div className='text-xs text-gray-900'>
                                         {activity.deviceInfo?.browserInfo || 'Unknown'}
                                      </div>
                                      <div className='text-xs text-gray-500'>
                                         {activity.deviceInfo?.platform || 'Unknown'}
                                      </div>
                                      {activity.deviceInfo?.isMobile && (
                                         <div className='text-xs text-blue-600'>📱</div>
                                      )}
                                   </td>
                                </tr>
                             );
                          })}
                       </tbody>
                    </table>
                 </div>
              )}

              {/* Pagination Controls */}
              {!loading && activities.length > 0 && (
                 <div className='mt-4 flex items-center justify-between'>
                    <div className='flex-1 flex justify-between sm:hidden'>
                       <button
                          onClick={() =>
                             setFilters((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))
                          }
                          disabled={pagination.page <= 1}
                          className='relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                       >
                          Previous
                       </button>
                       <button
                          onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                          disabled={!pagination.hasMore}
                          className='ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                       >
                          Next
                       </button>
                    </div>
                    <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
                       <div>
                          <p className='text-xs text-gray-700'>
                             Showing{' '}
                             <span className='font-medium'>
                                {(pagination.page - 1) * pagination.limit + 1}
                             </span>{' '}
                             to{' '}
                             <span className='font-medium'>
                                {Math.min(pagination.page * pagination.limit, pagination.total)}
                             </span>{' '}
                             of <span className='font-medium'>{pagination.total}</span> results
                          </p>
                       </div>
                       <div>
                          <nav
                             className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                             aria-label='Pagination'
                          >
                             <button
                                onClick={() =>
                                   setFilters((prev) => ({
                                      ...prev,
                                      page: Math.max(1, prev.page - 1),
                                   }))
                                }
                                disabled={pagination.page <= 1}
                                className='relative inline-flex items-center px-1.5 py-1.5 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                             >
                                <span className='sr-only'>Previous</span>
                                <svg
                                   className='h-4 w-4'
                                   xmlns='http://www.w3.org/2000/svg'
                                   viewBox='0 0 20 20'
                                   fill='currentColor'
                                   aria-hidden='true'
                                >
                                   <path
                                      fillRule='evenodd'
                                      d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                                      clipRule='evenodd'
                                   />
                                </svg>
                             </button>

                             {/* Page Numbers */}
                             {Array.from(
                                {
                                   length: Math.min(
                                      5,
                                      Math.ceil(pagination.total / pagination.limit)
                                   ),
                                },
                                (_, i) => {
                                   const pageNum = i + 1;
                                   return (
                                      <button
                                         key={pageNum}
                                         onClick={() =>
                                            setFilters((prev) => ({ ...prev, page: pageNum }))
                                         }
                                         className={`relative inline-flex items-center px-2 py-1.5 border text-xs font-medium ${
                                            pagination.page === pageNum
                                               ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                               : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                         }`}
                                      >
                                         {pageNum}
                                      </button>
                                   );
                                }
                             )}

                             <button
                                onClick={() =>
                                   setFilters((prev) => ({ ...prev, page: prev.page + 1 }))
                                }
                                disabled={!pagination.hasMore}
                                className='relative inline-flex items-center px-1.5 py-1.5 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                             >
                                <span className='sr-only'>Next</span>
                                <svg
                                   className='h-4 w-4'
                                   xmlns='http://www.w3.org/2000/svg'
                                   viewBox='0 0 20 20'
                                   fill='currentColor'
                                   aria-hidden='true'
                                >
                                   <path
                                      fillRule='evenodd'
                                      d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                                      clipRule='evenodd'
                                   />
                                </svg>
                             </button>
                          </nav>
                       </div>
                    </div>
                 </div>
              )}
           </div>
        </div>
     </div>
  );
};
